import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { bool, func } from 'prop-types';
import { Page, LayoutComposer } from '../../../components';

import TopbarContainer from '../../TopbarContainer/TopbarContainer';
import FooterContainer from '../../FooterContainer/FooterContainer';
import { FormattedMessage, injectIntl, intlShape } from '../../../util/reactIntl';
import uploadIllustration from '../../../assets/uploadNew.jpg';
import uploadIllustrationLR from '../../../assets/uploadLR.png';
import { LazyLoadImage } from 'react-lazy-load-image-component';

import { useDropzone } from 'react-dropzone';
import { v4 as uuidv4 } from 'uuid';
import { ClipLoader } from 'react-spinners';
import { connect } from 'react-redux';
import { uploadRequest, uploadSuccess, uploadError, clearItems } from '../SellPage.duck';

import { firebaseUploadItem, similarity } from '../../../util/api';
import { GoogleAnalyticsHandler } from '../../../analytics/handlers';
import { googleAnalyticsEnabled } from '../../../config/configAnalytics';
import { similaritySettings } from '../../../config/configBackend';
import { replaceUnderscoresWithHyphens } from '../../../util/string';

import css from './SellHomePage.module.css';

if (typeof window !== 'undefined') {
  require('react-lazy-load-image-component/src/effects/opacity.css');
}

const brandName = process.env.REACT_APP_BRAND_NAME;

const SellHomePage = props => {
  const {
    intl,
    uploadInProgress,
    uploadRequest,
    uploadSuccess,
    uploadError,
    clearItems,
    scrollingDisabled,
  } = props;
  const history = useHistory();

  const [loadingState, setLoadingState] = useState('SellHomePage.loadingState1');

  const analyticsHandler = new GoogleAnalyticsHandler();

  const topK = similaritySettings.topK;

  useEffect(() => {
    // Existing analytics code
    if (typeof _paq !== 'undefined') {
      _paq.push(['trackEvent', 'Selling', 'SellHomeView']);
    }
    if (googleAnalyticsEnabled == true) {
      analyticsHandler.trackCustomEvent('SellHomeView');
    }
  }, []);

  const handleButtonClick = () => {
    if (googleAnalyticsEnabled == true) {
      analyticsHandler.trackCustomEvent('SellUploadImage');
    }
  };

  // For this to work 100% correctly the FAQ page needs to have sections with the ids of the anchor tags that are the same as the linkAnchor id
  const handleMoreInfoClick = () => {
    const brandNameOriginal = process.env.REACT_APP_BRAND_NAME;
    const brandName = replaceUnderscoresWithHyphens(brandNameOriginal);
    const url = `${
      process.env.REACT_APP_MARKETPLACE_ROOT_URL
    }/p/faq-support-${brandName}#${intl.formatMessage({ id: 'SellHomePage.linkAnchor' })}`;
    window.open(url, '_blank');
  };

  const layoutAreas = `
    topbar
    main
    footer
  `;

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/webp': ['.webp'],
      'image/avif': ['.avif'],
    },
    onDrop: async acceptedFiles => {
      try {
        console.log('Starting file upload process...', {
          fileSize: acceptedFiles[0]?.size,
          fileType: acceptedFiles[0]?.type,
        });

        clearItems();
        sessionStorage.clear();
        window.sessionStorage.setItem('searchQuery', '');
        uploadRequest();
        const file = acceptedFiles[0];
        const idItem = uuidv4();
        const imageType = 'front';
        setLoadingState('SellHomePage.loadingState1');

        console.log('Calling firebaseUploadItem with:', {
          idItem,
          brandName,
          filePresent: !!file,
        });

        const uploadResponse = await firebaseUploadItem(file, idItem, brandName);
        console.log('Upload response received:', uploadResponse);

        setLoadingState('SellHomePage.loadingState2');
        const similarityRes = await similarity({ idItem, imageType, brandName, topK });

        try {
          const uploadedImageDownloadURL = uploadResponse.uploadedImageDownloadURL;
          if (similarityRes) {
            const jsonSimilarityResponse = JSON.stringify(similarityRes.jsonResponse);
            setLoadingState('SellHomePage.loadingState3');
            uploadSuccess({
              similarityResponseData: jsonSimilarityResponse,
              idItem: idItem,
              uploadedImageDownloadURL: uploadedImageDownloadURL,
            });
            window.sessionStorage.setItem('similarityResponseData', jsonSimilarityResponse);
            window.sessionStorage.setItem('idItem', idItem);
            window.sessionStorage.setItem('uploadedImageDownloadURL', uploadedImageDownloadURL);
            history.push('/sell/select');
          } else {
            console.error('Unexpected Similarity API error:', similarityRes);
            uploadError('Unexpected response code');
          }
        } catch (error) {
          console.error('Error during Similarity API call:', error);
          uploadError(error);
        }
      } catch (error) {
        console.error('Error in onDrop handler:', error);
        uploadError(error);
      }
    },
  });

  return (
    <Page
      title={intl.formatMessage({ id: 'SellHomePage.title' })}
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutComposer areas={layoutAreas} className={css.layout}>
        {() => (
          <>
            <TopbarContainer currentPage="SellHomePage" />
            <div>
              {uploadInProgress ? (
                <div className={css.loadingContainer}>
                  <ClipLoader color="var(--marketplaceColor)" size={65} />
                  <p>{intl.formatMessage({ id: loadingState })}</p>
                </div>
              ) : (
                <>
                  <div className={css.contentContainer}>
                    <LazyLoadImage
                      alt="Hero"
                      src={uploadIllustration}
                      placeholderSrc={uploadIllustrationLR}
                      className={css.heroImage}
                      effect="opacity"
                      loading="eager"
                      threshold={100}
                      decoding="async"
                      width="100%"
                      height="auto"
                      visibleByDefault={true}
                    />
                    <div className={css.darkOverlay}></div>
                    <div className={css.overlayContent}>
                      <h1 className={css.heading}>
                        {intl.formatMessage({ id: 'SellHomePage.heading' })}
                      </h1>
                      <div className={css.descriptionText}>
                        <FormattedMessage id="SellHomePage.text.part1" />
                      </div>
                      <ul className={css.bulletList}>
                        <li className={css.bulletPoint}>
                          <FormattedMessage id="SellHomePage.text.part2" />
                        </li>
                        <li className={css.bulletPoint}>
                          <FormattedMessage id="SellHomePage.text.part3" />
                        </li>
                        <li className={css.bulletPoint}>
                          <FormattedMessage id="SellHomePage.text.part4" />
                        </li>
                        <li className={css.bulletPoint}>
                          <FormattedMessage id="SellHomePage.text.part5" />
                        </li>
                      </ul>
                      <div className={css.moreInfoLink} onClick={handleMoreInfoClick}>
                        <FormattedMessage id="SellHomePage.moreInfoLink" />
                      </div>
                      <button
                        {...getRootProps({ onClick: handleButtonClick })}
                        className={css.button}
                      >
                        <input {...getInputProps()} />
                        {intl.formatMessage({ id: 'SellHomePage.button' })}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
            <FooterContainer />
          </>
        )}
      </LayoutComposer>
    </Page>
  );
};

SellHomePage.propTypes = {
  intl: intlShape.isRequired,
  uploadInProgress: bool.isRequired,
  uploadRequest: func.isRequired,
  uploadSuccess: func.isRequired,
  uploadError: func.isRequired,
  clearItems: func.isRequired,
  scrollingDisabled: bool.isRequired,
};

const mapStateToProps = state => {
  const { uploadInProgress, uploadError, similarityResponseData } = state.SellPage;
  return { uploadInProgress, uploadError, similarityResponseData };
};

const mapDispatchToProps = { uploadRequest, uploadSuccess, uploadError, clearItems };

export default connect(mapStateToProps, mapDispatchToProps)(injectIntl(SellHomePage));
